import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CatalogueGame, CatalogueTemplate } from 'src/app/models/bot/catalogue';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import {
  CatalogueListState,
  CatalogueViewStateService,
} from 'src/app/services/catalogue-view-state.service';
import { OAuthService } from 'angular-oauth2-oidc';

const PAGE_SIZE = 24;

@Component({
  selector: 'app-templates-catalogue',
  templateUrl: './templates-catalogue.component.html',
  styleUrls: ['./templates-catalogue.component.css'],
})
export class TemplatesCatalogueComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('loadMoreSentinel') loadMoreSentinel?: ElementRef<HTMLElement>;

  templates: CatalogueTemplate[] = [];
  games: CatalogueGame[] = [];
  total = 0;
  loading = true;
  loadingMore = false;
  searchControl = new FormControl('');
  selectedGameId: number | null = null;
  sort: 'newest' | 'likes' = 'newest';
  isBotOwner = false;
  isLoggedIn = false;

  private searchSub?: Subscription;
  private querySub?: Subscription;
  private syncingFromUrl = false;
  /** Filters last applied from the URL. Null until the first query-param emission. */
  private loadedQueryKey: string | null = null;
  private restoreState: CatalogueListState | null = null;
  private observer?: IntersectionObserver;
  private requestSeq = 0;

  constructor(
    private botService: BotResourceService,
    private oauthService: OAuthService,
    private route: ActivatedRoute,
    private router: Router,
    private catalogueViewState: CatalogueViewStateService
  ) {}

  get hasMore(): boolean {
    return this.templates.length < this.total;
  }

  ngOnInit(): void {
    this.botService.listCatalogueGames().subscribe((games) => (this.games = games));
    this.isLoggedIn = this.oauthService.hasValidAccessToken();
    if (this.isLoggedIn) {
      this.botService.getSession().subscribe({
        next: (session) => (this.isBotOwner = !!session.is_website_bot_owner),
        error: () => (this.isBotOwner = false),
      });
    }

    this.restoreState = this.catalogueViewState.consumePendingState();
    // Same route is reused when only the query string changes, including back/forward.
    this.querySub = this.route.queryParamMap.subscribe((params) => this.onQueryParams(params));
    this.searchSub = this.searchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(() => {
        if (this.syncingFromUrl) {
          return;
        }
        this.syncQueryParams();
      });
  }

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore();
        }
      },
      { root: null, rootMargin: '240px 0px', threshold: 0 }
    );
    this.observeSentinel();
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.querySub?.unsubscribe();
    this.observer?.disconnect();
  }

  private onQueryParams(params: { get(name: string): string | null }): void {
    const key = this.queryKey(params);
    const first = this.loadedQueryKey === null;
    this.applyQueryParams(params);
    if (!first && key === this.loadedQueryKey) {
      return;
    }
    if (!first) {
      this.restoreState = null;
    }
    this.loadedQueryKey = key;
    this.reloadFromStart();
  }

  private queryKey(params: { get(name: string): string | null }): string {
    const q = (params.get('q') || '').trim();
    const gameIdRaw = params.get('game_id');
    let gameId = '';
    if (gameIdRaw != null && gameIdRaw !== '') {
      const parsed = Number(gameIdRaw);
      gameId = Number.isFinite(parsed) ? String(parsed) : '';
    }
    const sort = params.get('sort') === 'likes' ? 'likes' : 'newest';
    return `${q}|${gameId}|${sort}`;
  }

  private applyQueryParams(params: { get(name: string): string | null }): void {
    const q = params.get('q') || '';
    const gameIdRaw = params.get('game_id');
    const sort = params.get('sort');

    this.syncingFromUrl = true;
    this.searchControl.setValue(q, { emitEvent: false });
    this.syncingFromUrl = false;

    if (gameIdRaw != null && gameIdRaw !== '') {
      const parsed = Number(gameIdRaw);
      this.selectedGameId = Number.isFinite(parsed) ? parsed : null;
    } else {
      this.selectedGameId = null;
    }

    this.sort = sort === 'likes' ? 'likes' : 'newest';
  }

  private syncQueryParams(): void {
    const queryParams: Record<string, string | null> = {
      q: this.searchControl.value?.trim() || null,
      game_id: this.selectedGameId != null ? String(this.selectedGameId) : null,
      sort: this.sort !== 'newest' ? this.sort : null,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  reloadFromStart(): void {
    this.templates = [];
    this.total = 0;
    this.catalogueViewState.setLoadedCount(0);
    const targetCount = this.restoreState?.loadedCount || PAGE_SIZE;
    this.fetchPage(0, Math.min(Math.max(PAGE_SIZE, targetCount), 100), true);
  }

  loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasMore) {
      return;
    }
    this.fetchPage(this.templates.length, PAGE_SIZE, false);
  }

  private fetchPage(offset: number, limit: number, replace: boolean): void {
    const seq = ++this.requestSeq;
    if (replace) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }

    this.botService
      .listCatalogueTemplates({
        q: this.searchControl.value || undefined,
        game_id: this.selectedGameId,
        sort: this.sort,
        limit,
        offset,
      })
      .subscribe({
        next: (response) => {
          if (seq !== this.requestSeq) {
            return;
          }
          this.total = response.total;
          this.templates = replace
            ? response.items
            : [...this.templates, ...response.items];
          this.catalogueViewState.setLoadedCount(this.templates.length);
          this.loading = false;
          this.loadingMore = false;

          // Keep filling until we have enough rows for the previous scroll position.
          if (
            this.restoreState &&
            this.templates.length < this.restoreState.loadedCount &&
            this.hasMore
          ) {
            this.fetchPage(this.templates.length, PAGE_SIZE, false);
            return;
          }

          this.restoreScrollAfterRender();
          setTimeout(() => this.observeSentinel());
        },
        error: () => {
          if (seq !== this.requestSeq) {
            return;
          }
          if (replace) {
            this.templates = [];
            this.total = 0;
            this.catalogueViewState.setLoadedCount(0);
          }
          this.loading = false;
          this.loadingMore = false;
          this.restoreScrollAfterRender();
        },
      });
  }

  private observeSentinel(): void {
    if (!this.observer) {
      return;
    }
    this.observer.disconnect();
    const el = this.loadMoreSentinel?.nativeElement;
    if (el && this.hasMore) {
      this.observer.observe(el);
    }
  }

  private restoreScrollAfterRender(): void {
    const y = this.restoreState?.scrollY;
    if (y == null || y <= 0) {
      this.restoreState = null;
      return;
    }
    // Wait until the grid replaces the loading spinner so the page has height again.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
        this.restoreState = null;
        this.observeSentinel();
      });
    });
  }

  onGameChange(): void {
    this.syncQueryParams();
  }

  onSortChange(): void {
    this.syncQueryParams();
  }
}
